<template>
  <div class="live-console-container">
    <el-alert
      v-if="liveContextStatus !== 'ready'"
      :title="liveContextError"
      :type="liveContextStatus === 'loading' ? 'info' : 'warning'"
      :closable="false"
      show-icon
    />
  
    <div class="console-content">
      <!-- 左侧视频播放区域 -->
      <div class="left-panel">
        <!-- 操作按钮 -->
        <div class="action-buttons">
          <el-button 
            v-if="liveInfo.state === 1"
            :disabled="!canUseLive"
            type="danger" 
            size="medium" 
            @click="handlePauseLive"
          >
            暂停直播
          </el-button>
          <el-button 
            v-if="liveInfo.state === 2"
            :disabled="!canUseLive"
            type="success" 
            size="medium" 
            @click="handleResumeLive"
          >
            继续直播
          </el-button>
          <el-button type="danger" size="medium" :disabled="!canUseLive" @click="handleEndLive">下播</el-button>
          <el-button type="primary" size="medium" :disabled="!canUseLive" @click="handlePushRemind">推送提醒</el-button>
        </div>

        <!-- 直播信息 -->
        <div class="live-info">
          <div class="info-item">
            <span class="label">直播名称：</span>
            <span class="value">{{ liveInfo.title }}</span>
            <span class="label">主播信息：</span>
            <span class="value">{{ liveInfo.anchorInfo }}</span>
          </div>
          <div class="info-item">
            <span class="label">直播人数：</span>
            <span class="value">{{ totalViewerCount }}人</span>
            <span class="label">直播时间：</span>
            <span class="value">{{ liveInfo.startTime }}</span>
            <!-- <span class="label">观看权限：</span>
            <span class="value">{{ liveInfo.viewPermission }}</span> -->
          </div>
 
        </div>

        <!-- 视频播放器 -->
        <div class="video-player" :class="{ 'vertical-mode': liveInfo.liveType === 1, 'horizontal-mode': liveInfo.liveType === 2 }">
          <div class="video-container">
            <div :key="playerDomKey" class="player-mount">
              <video
                ref="playerElement"
                id="live-player"
                width="100%"
                height="100%"
                preload="auto"
                playsinline
                webkit-playsinline
                controls
                muted
                @dblclick="toggleFullscreen"
              ></video>
            </div>
            <!-- 右上角全屏按钮 -->
            <div v-if="player" class="fullscreen-btn" @click="toggleFullscreen" title="全屏/退出全屏">
              <i class="el-icon-full-screen"></i>
            </div>
            <!-- 播放器错误提示。这块曾被整段注释掉，导致播放失败时只剩一个黑框，
                 「横屏直播控制台连不到直播间」(BUG-2026-866644) 完全无从判断是
                 地址失效、网络问题还是 license 问题。恢复出来用于诊断，别再注释。
                 playerError 的取值都已在 JS 里映射成中文文案（见 createPlayer 的错误处理）。 -->
            <div v-if="playerError" class="player-error-overlay">
              <div class="error-content">
                <i class="el-icon-warning"></i>
                <p>{{ playerError }}</p>
                <el-button
                  v-if="canUseLive && playerError === '请点击播放按钮开始播放'"
                  type="primary"
                  @click="startPlay"
                  class="play-button"
                >
                  <i class="el-icon-video-play"></i> 开始播放
                </el-button>
                <el-button
                  v-else-if="canUseLive"
                  type="primary"
                  @click="retryPlay"
                >
                  重试播放
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧管理区域 -->
      <div class="right-panel">
        <!-- 管理标签页 -->
        <div class="management-tabs">
          <el-tabs v-model="activeTab" @tab-click="handleTabClick">
            <el-tab-pane label="直播间" name="room"></el-tab-pane>
            <!-- <el-tab-pane label="福袋" name="luckyBag"></el-tab-pane>
            <el-tab-pane label="商品" name="goods"></el-tab-pane> -->
            <!-- <el-tab-pane label="打赏记录" name="rewards"></el-tab-pane> -->
            <el-tab-pane label="用户" name="users">
              <!-- 用户统计信息 -->
              <!-- <div class="user-stats">
                <div class="stat-item">
                  <span class="stat-label">总观看人数：</span>
                  <span class="stat-value">{{ userStats.totalViewers }}人</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">总观看时长：</span>
                  <span class="stat-value">{{ userStats.totalDuration }}分钟</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">平均观看时长：</span>
                  <span class="stat-value">{{ userStats.avgDuration }}分钟</span>
                </div>
              </div> -->
              
              <!-- 搜索框 -->
              <div class="search-box">
                <el-input
                  v-model="userQuery.keyWord"
                  placeholder="请输入关键字搜索（姓名/手机号）"
                  size="small"
                  clearable
                  @keyup.enter.native="handleSearchUsers"
                  @clear="handleSearchUsers"
                >
                  <el-button slot="append" icon="el-icon-search" @click="handleSearchUsers"></el-button>
                </el-input>
              </div>
              
              <!-- 用户列表 -->
              <div class="user-list">
                <el-table :data="userList" style="width: 100%" size="mini">
                  <el-table-column prop="userInfo" label="用户信息" width="140">
                    <template slot-scope="{row}">
                      <div class="user-info">
                        <span class="user-name">{{ row.name }}</span>
                        <span class="user-level">{{ row.positionName }}</span>
                        <span class="user-phone">{{ row.pho }}</span>
                      </div>
                    </template>
                  </el-table-column>
                  <!-- <el-table-column prop="positionName" label="所属业务线" width="120">
                    <template slot-scope="{row}">
                      {{ row.positionName }}
                    </template>
                  </el-table-column> -->
                  <el-table-column prop="viewDuration" label="观看时长" width="100">
                    <template slot-scope="{row}">
                      {{ calculateViewDuration(row) }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="jionTime" label="进入直播间时间" width="150">
                    <template slot-scope="{row}">
                      {{ row.jionTime }}
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="100" fixed="right">
                    <template slot-scope="{row}">
                      <el-button 
                        v-if="row.isForbiddenSpeech === 0" 
                        size="mini" 
                        type="danger" 
                        @click="handleForbiddenUser(row, 1)"
                      >
                        禁言
                      </el-button>
                      <el-button 
                        v-else 
                        size="mini" 
                        type="success" 
                        @click="handleForbiddenUser(row, 0)"
                      >
                        解禁
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-tab-pane>
            <el-tab-pane label="小黑屋" name="blacklist">
              <!-- 搜索框 -->
              <div class="search-box">
                <el-input
                  v-model="userQuery.keyWord"
                  placeholder="请输入关键字搜索（姓名/手机号）"
                  size="small"
                  clearable
                  @keyup.enter.native="handleSearchBlacklist"
                  @clear="handleSearchBlacklist"
                >
                  <el-button slot="append" icon="el-icon-search" @click="handleSearchBlacklist"></el-button>
                </el-input>
              </div>
              
              <!-- 小黑屋用户列表 -->
              <div class="user-list">
                <el-table :data="blacklistUsers" style="width: 100%" size="mini">
                  <el-table-column prop="userInfo" label="用户信息" width="140">
                    <template slot-scope="{row}">
                      <div class="user-info">
                        <span class="user-name">{{ row.name }}</span>
                        <span class="user-level">{{ row.positionName }}</span>
                        <span class="user-phone">{{ row.pho }}</span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="viewDuration" label="观看时长" width="100">
                    <template slot-scope="{row}">
                      {{ calculateViewDuration(row) }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="jionTime" label="进入直播间时间" width="150">
                    <template slot-scope="{row}">
                      {{ row.jionTime }}
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="100" fixed="right">
                    <template slot-scope="{row}">
                      <el-button 
                        size="mini" 
                        type="success" 
                        @click="handleForbiddenUser(row, 0)"
                      >
                        移除
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>

        <!-- 聊天区域 - 只在直播间标签页显示 -->
        <div class="chat-area" v-if="activeTab === 'room'">
          <div class="chat-messages" ref="chatMessages">
            <div 
              v-for="(message, index) in chatMessages" 
              :key="index" 
              :class="['message-item', message.type]"
            >
              <span v-if="message.timestamp" class="timestamp">{{ message.timestamp }}</span>
              <span 
                v-if="message.user && message.type === 'user-message'" 
                class="user clickable-user" 
                @click="handleClickUserName(message)"
              >
                {{ message.user }}:
              </span>
              <span v-else-if="message.user" class="user">{{ message.user }}:</span>
              <span class="content">{{ message.content }}</span>
            </div>
          </div>

          <!-- 消息输入区域 -->
          <div class="message-input">
            <el-input
              v-model="messageText"
              :disabled="!canUseLive"
              placeholder=""
              @keyup.enter="handleSendMessage"
            ></el-input>
            <el-button type="success" :disabled="!canUseLive" @click="handleSendMessage">发送</el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import liveManageApi from '@/api/live/liveManage'
import liveBlessingBagApi from '@/api/live/liveBlessingBag'
// TCPlayer 将通过动态加载的方式引入
import { getPlayerConfig, getBestPlayUrl, toProxyUrl, LIVE_CONFIG } from '@/config/live'
import request from '@/utils/request'
import TimMessage from '@/utils/tim-message'
import { imIdUserId } from '@/utils/imId'

// 不在中控消息列表展示的自定义消息，避免刷屏淹没有效消息
// live-enter-msg：客户进入直播间提醒
// customer_live_addVirtual：虚拟人数变更，无 description，只会显示成"[自定义消息]"
const HIDDEN_BUSINESS_IDS = ['live-enter-msg', 'customer_live_addVirtual']
const emptyLiveInfo = () => ({ id: '', title: '', anchorInfo: '', viewerCount: 0, lookUserVirtualcount: 0, startTime: '', viewPermission: '', state: null })

export default {
  name: 'LiveConsole',
  data() {
    return {
      activeTab: 'room',
      messageText: '',
      // TCPlayer 授权地址（支持路由参数与环境变量注入）
      licenseUrl: '',
      liveInfo: emptyLiveInfo(),
      liveContextStatus: 'loading',
      liveContextError: '正在加载直播信息…',
      liveContextGeneration: 0,
      liveContextDisposed: false,
      liveAdminList: [], // 直播管理员列表
      chatMessages: [],
      im: null,
      imReady: false,
      imGroupId: '',
      imOnlineCount: 0, // IM 在线人数
      imCountTimer: null, // IM 在线人数更新定时器
      luckyBagCountdownTimers: {}, // 福袋倒计时定时器对象，key为福袋ID
      luckyBagLotteryRequested: {}, // 记录已请求开奖的福袋ID，避免重复请求
      // 用户相关数据
      userList: [],
      blacklistUsers: [], // 小黑屋用户列表
      userStats: {
        totalViewers: 0,
        totalDuration: 0,
        avgDuration: 0
      },
      userQuery: {
        keyWord: '',
        liveId: null,
        pageNo: 1,
        pageSize: 20
      },
      player: null,
      playerError: '',
      playerDomKey: 0,
      playerGeneration: 0,
      playerClosed: false,
      playerCurrentUrl: '',
      playerAttemptedUrls: [],
      playerPlaySequence: 0,
      playerFallbackTimer: null,
      playerReturnTimer: null,
      hasTriedAllUrls: false,
      streamUrls: {
        playFlvUri: '',

        playWebRtc: '',
        pushObsUri: '',
        pushRtmpOverQuicUri: '',
        pushRtmpOverSrtUri: '',
        pushRtmpUri: '',
        pushSrtUri: '',
        pushWebRtcUri: ''
      }
    }
  },
  computed: {
    routeLiveId() {
      const id = this.$route.query.id
      return (typeof id === 'string' || typeof id === 'number') && /^[1-9]\d*$/.test(String(id)) ? String(id) : ''
    },
    canUseLive() {
      return !this.liveContextDisposed && this.liveContextStatus === 'ready' && this.routeLiveId === String(this.liveInfo.id) && [1, 2].includes(this.liveInfo.state)
    },
    // 计算总观看人数（虚拟人数 + 真实观看人数 + IM 在线人数）
    totalViewerCount() {
      const virtualCount = this.liveInfo.lookUserVirtualcount || 0
      const realCount = this.liveInfo.viewerCount || 0
      const imCount = this.imOnlineCount || 0
      return virtualCount + realCount + imCount
    }
  },
  watch: {
    '$route.query.id'() {
      this.openLiveContext()
    }
  },
  mounted() {
    this.openLiveContext()
  },
  beforeDestroy() {
    this.liveContextDisposed = true
    this.liveContextGeneration++
    this.clearLiveTasks()
  },
  methods: {
    clearLiveTasks() {
      this.stopPlayer()
      if (this.playerReturnTimer !== null) {
        clearTimeout(this.playerReturnTimer)
        this.playerReturnTimer = null
      }
      // 清除 IM 在线人数与福袋倒计时。
      if (this.imCountTimer) {
        clearInterval(this.imCountTimer)
        this.imCountTimer = null
      }
      Object.keys(this.luckyBagCountdownTimers).forEach(bagId => {
        clearInterval(this.luckyBagCountdownTimers[bagId])
      })
      this.luckyBagCountdownTimers = {}
      if (this.im) {
        this.im.cleanup && this.im.cleanup()
        this.im = null
      }
      this.imReady = false
    },
    isCurrentLiveContext(generation, id = this.liveInfo.id) {
      return !this.liveContextDisposed && generation === this.liveContextGeneration && String(id) === this.routeLiveId
    },
    async openLiveContext() {
      if (this.liveContextDisposed) return
      const generation = ++this.liveContextGeneration
      this.clearLiveTasks()
      this.liveInfo = emptyLiveInfo()
      this.liveAdminList = []
      this.streamUrls = {}
      this.imGroupId = ''
      this.imOnlineCount = 0
      this.chatMessages = []
      this.messageText = ''
      this.userList = []
      this.blacklistUsers = []
      this.userStats = { totalViewers: 0, totalDuration: 0, avgDuration: 0 }
      this.luckyBagLotteryRequested = {}
      this.liveContextStatus = 'loading'
      this.liveContextError = '正在加载直播信息…'
      this.playerError = ''
      await this.loadLiveInfoFromRoute(generation)
      if (!this.isCurrentLiveContext(generation) || this.liveContextStatus !== 'ready') return
      if (!this.canUseLive) {
        this.playerError = this.liveInfo.state === 4 ? '直播已取消' : (this.liveInfo.state === 3 ? '直播已结束' : '直播尚未开始')
        return
      }
      this.playerClosed = false
      this.initPlayer()
      this.loadLuckyBagList()
    },
    // 从路由参数加载直播信息
    async loadLiveInfoFromRoute(generation) {
      const query = this.$route.query
      const id = this.routeLiveId
      console.log('路由参数:', query)
      // 读取 licenseUrl，优先使用路由参数，其次使用环境变量
      this.licenseUrl = (process.env.VUE_APP_TCPLAYER_LICENSE_URL || '').trim()
      
      // 如果有liveId参数，调用接口获取直播详情
      if (!id) {
        this.liveContextStatus = 'missing'
        this.liveContextError = query.id == null || query.id === '' ? '未指定直播，请从直播列表进入' : '直播参数无效，请从直播列表重新进入'
        return
      }
      if (id) {
        console.log('检测到liveId参数，准备调用接口:', query.id)
        try {
          const response = await liveManageApi.getLiveById(id)
          if (!this.isCurrentLiveContext(generation, id)) return
          if (response.code === '0' && response.data && response.data.livePO) {
            const { livePO, liveAnchorPO, liveAdminPOList } = response.data
            const state = livePO.state == null || livePO.state === '' ? null : Number(livePO.state)
            if (String(livePO.id) !== id || ![0, 1, 2, 3, 4].includes(state)) throw new Error('直播详情不匹配或状态未知')
            
            // 设置直播信息
            this.liveInfo = {
              id: livePO.id,
              title: livePO.title || '未知直播间',
              anchorInfo: liveAnchorPO ? liveAnchorPO.liveName : '未知主播',
              viewerCount: livePO.lookUserCount || 0,
              startTime: livePO.startTime || '',
              viewPermission: query.viewPermission || '公开观看',
              // 新增字段
              liveImg: livePO.liveImg,
              liveType: livePO.liveType || 1, // 直播类型：1-竖屏，2-横屏
              likeCount: livePO.likeCount,
              shareCount: livePO.shareCount,
              speakCount: livePO.speakCount,
              rewardCount: livePO.rewardCount,
              rewardCashAmount: livePO.rewardCashAmount,
              rewardXdbAmount: livePO.rewardXdbAmount,
              killOrderCount: livePO.killOrderCount,
              yueOrderCount: livePO.yueOrderCount,
              tuitionOrderCount: livePO.tuitionOrderCount,
              timeSpan: livePO.timeSpan,
              state,
              lookUserVirtualcount: livePO.lookUserVirtualcount || 0
            }
            
            // 设置流地址
            this.streamUrls = {
              playFlvUri: livePO.playFlvUri || '',
              playHlsUri: livePO.playHlsUri || '',
              playRtmpUri: livePO.playRtmpUri || '',
              playWebRtc: livePO.playWebRtc || '',
              pushObsUri: livePO.pushObsUri || '',
              pushRtmpOverQuicUri: livePO.pushRtmpOverQuicUri || '',
              pushRtmpOverSrtUri: livePO.pushRtmpOverSrtUri || '',
              pushRtmpUri: livePO.pushRtmpUri || '',
              pushSrtUri: livePO.pushSrtUri || '',
              pushWebRtcUri: livePO.pushWebRtcUri || ''
            }
            
            // 保存管理员信息
            this.liveAdminList = liveAdminPOList || []
            
            // 从接口返回的数据中获取群ID
            this.imGroupId = livePO.imGroupId || ''
            this.liveContextStatus = 'ready'
            this.liveContextError = ''
            
            console.log('从接口获取的直播详情:', response.data)
            console.log('livePO数据:', livePO)
            console.log('playFlvUri:', livePO.playFlvUri)
            console.log('playWebRtc:', livePO.playWebRtc)
            console.log('设置的播放地址:', this.streamUrls)
            console.log('设置的直播信息:', this.liveInfo)
            return
          }
          throw new Error('直播详情加载失败')
        } catch (error) {
          if (!this.isCurrentLiveContext(generation, id)) return
          this.liveContextStatus = 'error'
          this.liveContextError = '直播信息加载失败，请返回直播列表重试'
          console.error('获取直播详情失败:', error)
        }
      }
      console.log('从路由加载的播放地址:', this.streamUrls)
      console.log('从路由加载直播信息:', this.liveInfo)
    },
    
    // 初始化播放器
    initPlayer() {
      if (!this.canUseLive || this.playerClosed) return
      this.createPlayer()
      
      // 初始化IM消息
      this.initIMMessage()
    },
    
    
    // 创建播放器
    async createPlayer(playUrl = getBestPlayUrl(this.streamUrls), resetAttempts = true, autoplay = false) {
      if (!this.canUseLive || this.playerClosed) return
      playUrl = toProxyUrl(playUrl)
      this.cancelPlayerFallback()
      const generation = ++this.playerGeneration
      this.playerPlaySequence++
      this.disposePlayer()
      if (resetAttempts) {
        this.playerAttemptedUrls = []
        this.hasTriedAllUrls = false
      }
      this.playerCurrentUrl = playUrl || ''
      this.playerError = autoplay ? '正在加载直播…' : ''
      // TCPlayer.dispose() 会移除视频节点，必须让 Vue 重建整个局部挂载容器。
      this.playerDomKey++
      await this.$nextTick()
      if (this.playerClosed || generation !== this.playerGeneration) return
      if (!this.$refs.playerElement) {
        this.playerError = '播放器初始化失败'
        return
      }
      if (!playUrl) {
        this.playerError = '暂无播放地址，请检查直播状态'
        this.$message.warning('暂无播放地址')
        return
      }
      this.recordPlayerUrl(playUrl)
      await this.initTCPlayer(playUrl, generation, autoplay)
    },

    cancelPlayerFallback() {
      if (this.playerFallbackTimer !== null) {
        clearTimeout(this.playerFallbackTimer)
        this.playerFallbackTimer = null
      }
    },

    disposePlayer() {
      const player = this.player
      this.player = null
      if (player) {
        try {
          player.dispose()
        } catch (error) {
          console.warn('清理播放器失败', error)
        }
      }
    },

    stopPlayer() {
      this.playerClosed = true
      this.cancelPlayerFallback()
      this.playerGeneration++
      this.playerPlaySequence++
      this.disposePlayer()
    },

    isCurrentPlayer(player, generation) {
      return this.canUseLive && !this.playerClosed && generation === this.playerGeneration && player === this.player
    },

    recordPlayerUrl(url) {
      this.playerCurrentUrl = url
      const normalizedUrl = toProxyUrl(url)
      if (!this.playerAttemptedUrls.includes(normalizedUrl)) {
        this.playerAttemptedUrls.push(normalizedUrl)
      }
    },

    getFallbackUrls() {
      return [...new Set([this.streamUrls.playWebRtc, toProxyUrl(this.streamUrls.playFlvUri)]
        .filter(url => url && url.trim()))]
    },

    getNextPlayerUrl() {
      const currentUrl = toProxyUrl(this.playerCurrentUrl)
      return this.getFallbackUrls().find(url => url !== currentUrl && !this.playerAttemptedUrls.includes(url))
    },

    schedulePlayerFallback(delay = 2000) {
      if (!this.canUseLive || this.playerClosed || !this.player) return
      if (!this.getNextPlayerUrl()) {
        this.cancelPlayerFallback()
        this.playerError = '所有播放地址都不可用，请检查直播状态'
        if (!this.hasTriedAllUrls) this.$message.error(this.playerError)
        this.hasTriedAllUrls = true
        return
      }
      if (delay === 0) {
        this.tryFallbackUrl()
        return
      }
      if (this.playerFallbackTimer !== null) return
      const player = this.player
      const generation = this.playerGeneration
      const url = this.playerCurrentUrl
      this.playerFallbackTimer = setTimeout(() => {
        this.playerFallbackTimer = null
        if (this.isCurrentPlayer(player, generation) && url === this.playerCurrentUrl) this.tryFallbackUrl()
      }, delay)
    },

    requestPlayerPlay() {
      if (!this.canUseLive || this.playerClosed || !this.player) return
      const player = this.player
      const generation = this.playerGeneration
      const url = this.playerCurrentUrl
      const sequence = ++this.playerPlaySequence
      const onFailure = error => {
        if (!this.isCurrentPlayer(player, generation) || url !== this.playerCurrentUrl || sequence !== this.playerPlaySequence) return
        // 切源造成的旧 play 中断不是新的播放失败，也不能抹掉已经显示的错误。
        if (error && (error.name === 'AbortError' || /interrupted by a call to pause/i.test(error.message || ''))) return
        this.playerError = '播放失败: ' + ((error && error.message) || '未知错误')
        this.schedulePlayerFallback()
      }
      try {
        const playback = player.play()
        // 5.1.0 切源/未就绪分支返回 void；只监听真实 Promise 的失败，成功由 playing 事件确认。
        if (playback && typeof playback.then === 'function') return playback.then(undefined, onFailure)
      } catch (error) {
        onFailure(error)
      }
    },

    switchPlayerUrl(url, autoplay = true) {
      if (!this.canUseLive || this.playerClosed || !this.player) return
      url = toProxyUrl(url)
      this.cancelPlayerFallback()
      this.playerPlaySequence++
      this.recordPlayerUrl(url)
      this.playerError = '正在切换播放地址…'
      try {
        this.player.src(url)
      } catch (error) {
        // 同步切源异常才重建，保留本轮已尝试地址，不能循环重置到首选地址。
        return this.createPlayer(url, false, autoplay)
      }
      if (autoplay) return this.requestPlayerPlay()
    },
    
    // 动态加载 TCPlayer
    async loadTCPlayer() {
      return new Promise((resolve, reject) => {
        // 检查是否已经加载
        if (window.TCPlayer) {
          resolve()
          return
        }
        
        // 获取当前页面的基础路径
        const basePath = process.env.BASE_URL || '/'
        console.log('当前基础路径:', basePath)
        console.log('NODE_ENV:', process.env.NODE_ENV)
        
        // 根据环境构建资源路径
        let cssPath, jsPath
        if (process.env.NODE_ENV === 'development') {
          // 开发环境：直接使用 /release/
          cssPath = '/release/tcplayer.min.css'
          jsPath = '/release/tcplayer.v5.1.0.min.js'
        } else if (basePath === '/admin-vue/') {
          // 测试环境（staging）：使用二级目录
          cssPath = `${basePath}release/tcplayer.min.css`
          jsPath = `${basePath}release/tcplayer.v5.1.0.min.js`
        } else {
          // 生产环境（production）：无二级目录，直接使用 /release/
          cssPath = '/release/tcplayer.min.css'
          jsPath = '/release/tcplayer.v5.1.0.min.js'
        }
        
        console.log('CSS路径:', cssPath)
        console.log('JS路径:', jsPath)
        
        // 加载 CSS - 使用动态路径支持二级目录
        const cssLink = document.createElement('link')
        cssLink.rel = 'stylesheet'
        cssLink.href = cssPath
        document.head.appendChild(cssLink)
        
        // 加载 JS - 使用动态路径支持二级目录
        const script = document.createElement('script')
        script.src = jsPath
        script.onload = () => {
          console.log('TCPlayer 加载成功')
          resolve()
        }
        script.onerror = () => {
          console.error('TCPlayer 加载失败')
          reject(new Error('TCPlayer 加载失败'))
        }
        document.head.appendChild(script)
      })
    },
    
    // 初始化 TCPlayer（改进版）
    async initTCPlayer(playUrl, generation, autoplay) {
      console.log('播放地址:', playUrl)
      
      try {
        console.log('开始初始化 TCPlayer...')
        
        // 确保 DOM 元素存在
        const playerElement = this.$refs.playerElement
        if (!playerElement) {
          throw new Error('播放器 DOM 元素不存在')
        }
      
        // 计算有效的 licenseUrl：路由参数 > 环境变量 > 配置默认
        const effectiveLicenseUrl = (this.licenseUrl || process.env.VUE_APP_TCPLAYER_LICENSE_URL || LIVE_CONFIG.PLAYER_LICENSE_URL || '').trim()
        if (!effectiveLicenseUrl) {
          this.playerError = '缺少 licenseUrl，请配置 VUE_APP_TCPLAYER_LICENSE_URL 或通过路由传入 licenseUrl'
          console.error('TCPlayer 缺少 licenseUrl，初始化中止')
          return
        }
        
        // 动态加载 TCPlayer
        await this.loadTCPlayer()
        if (!this.canUseLive || this.playerClosed || generation !== this.playerGeneration) return
        if (!this.$refs.playerElement) throw new Error('播放器 DOM 元素不存在')
        
        // 使用配置化的播放器配置
        const playerConfig = getPlayerConfig(playUrl, effectiveLicenseUrl)
        // 覆盖为填充容器模式
        playerConfig.fluid = false
        playerConfig.fill = true
        
        const player = window.TCPlayer('live-player', playerConfig)
        this.player = player
        const playSequence = this.playerPlaySequence
        
        // 改进的错误处理
        player.on('error', (error = {}) => {
          if (!this.isCurrentPlayer(player, generation)) return
          console.error('播放器错误:', error)
          
          // 解析错误类型
          let errorMessage = '播放失败'
          let errorCode = error.code || (error.data && error.data.code)
          
          // 检查错误消息中是否包含404
          const errorMsg = error.message || ''
          if (errorMsg.includes('404') || errorMsg.includes('Not Found')) {
            errorMessage = '直播流不存在，可能直播已结束或地址错误'
            errorCode = 404
          } else {
            switch (errorCode) {
              case 55:
                errorMessage = '网络连接失败，请检查网络或直播状态'
                break
              case 14:
                errorMessage = '播放地址不可用，可能是直播已结束'
                break
              case 4:
                errorMessage = '视频格式不支持'
                break
              case 2:
                errorMessage = '网络错误，无法加载视频流'
                break
              default:
                errorMessage = `播放错误 (${errorCode || '未知'})`
            }
          }
          
          this.playerError = errorMessage
          if (!this.hasTriedAllUrls) this.$message.error(errorMessage)
          this.schedulePlayerFallback(errorCode === 404 ? 0 : 2000)
        })
        
        player.on('playing', () => {
          if (!this.isCurrentPlayer(player, generation)) return
          console.log('播放器开始播放')
          this.cancelPlayerFallback()
          this.playerError = ''
        })
        
        player.on('loadstart', () => {
          console.log('开始加载视频')
        })
        
        player.on('canplay', () => {
          console.log('视频可以播放')
        })
        
        player.on('waiting', () => {
          console.log('视频缓冲中...')
        })

        player.ready(() => {
          if (!this.isCurrentPlayer(player, generation)) return
          try {
            player.removeClass && player.removeClass('vjs-fluid')
            player.addClass && player.addClass('vjs-fill')
          } catch (error) {
            console.warn('设置填充模式失败，不影响播放', error)
          }
          if (playSequence !== this.playerPlaySequence || playUrl !== this.playerCurrentUrl) return
          if (autoplay) this.requestPlayerPlay()
          else if (!this.playerError) this.playerError = '请点击播放按钮开始播放'
        })
        
      } catch (error) {
        if (this.playerClosed || generation !== this.playerGeneration) return
        console.error('播放器初始化失败:', error)
        this.playerError = '播放器初始化失败: ' + error.message
        this.$message.error('播放器初始化失败: ' + error.message)
      }
    },
    
    
    // 验证播放地址（改进版）
    async validatePlayUrl(url) {
      try {
        // WebRTC 协议不需要 HTTP 验证
        if (url.includes('webrtc://')) {
          console.log('WebRTC 地址格式正确，可以尝试播放')
          return true
        }
        
        // RTMP 协议不需要 HTTP 验证
        if (url.includes('rtmp://')) {
          console.log('RTMP 地址格式正确，可以尝试播放')
          return true
        }
        
        // HTTP-FLV 协议验证
        if (url.includes('.flv')) {
          console.log('HTTP-FLV 地址格式正确，可以尝试播放')
          return true
        }
        
        // HLS 协议验证
        if (url.includes('.m3u8')) {
          console.log('HLS 地址格式正确，可以尝试播放')
          return true
        }
        
        // 对于其他 HTTP/HTTPS 地址，进行基本格式验证
        if (url.startsWith('http://') || url.startsWith('https://')) {
          console.log('HTTP/HTTPS 地址格式正确，可以尝试播放')
          return true
        }
        
        console.warn('未知的播放地址格式:', url)
        return false
      } catch (error) {
        console.warn('地址验证失败:', url, error)
        return false
      }
    },
    
    // 尝试备用播放地址（改进版）
    // ⚠️ flv 必须经 toProxyUrl 换成 /liveproxy/：原地址是 http，管理端是 https 页面，
    //    浏览器会按混合内容拦掉，换 https 又过不了腾讯云那边的证书。
    tryFallbackUrl() {
      if (!this.canUseLive || this.playerClosed || !this.player) return
      this.cancelPlayerFallback()
      const nextUrl = this.getNextPlayerUrl()
      if (!nextUrl) return this.schedulePlayerFallback()
      return this.switchPlayerUrl(nextUrl)
    },
    
    // 设置播放地址
    setPlayUrl() {
      if (this.canUseLive && !this.playerClosed && this.player) {
        const playUrl = getBestPlayUrl(this.streamUrls)
        if (playUrl) {
          this.playerAttemptedUrls = []
          this.hasTriedAllUrls = false
          return this.switchPlayerUrl(playUrl, false)
        }
      }
    },
    
    // 关闭控制台
    handleClose() {
      this.$router.go(-1)
    },
    
    // 暂停直播
    handlePauseLive() {
      if (!this.canUseLive || this.liveInfo.state !== 1) return
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      this.$confirm('确认暂停直播？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || this.liveInfo.state !== 1) return
        
        liveManageApi.updateLiveState({
          liveId: id,
          state: 2
        }).then(response => {
          if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive) return
          console.log('暂停直播响应:', response)
          if (response.data && response.data.state === 2) {
            this.liveInfo.state = 2
            this.$message.success('直播已暂停')
          } else {
            this.$message.error('操作失败，前往App操作')
          }
        }).catch(() => {
          this.$message.error('操作失败，前往App操作')
        })
      }).catch(() => {
        this.$message.info('已取消操作')
      })
    },
    
    // 继续直播
    handleResumeLive() {
      if (!this.canUseLive || this.liveInfo.state !== 2) return
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      this.$confirm('确认继续直播？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || this.liveInfo.state !== 2) return
        
        liveManageApi.updateLiveState({
          liveId: id,
          state: 1
        }).then(response => {
          if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive) return
          console.log('继续直播响应:', response)
          if (response.data && response.data.state === 1) {
            this.liveInfo.state = 1
            this.$message.success('直播已继续')
          } else {
            this.$message.error('操作失败，前往App操作')
          }
        }).catch(() => {
          this.$message.error('操作失败，前往App操作')
        })
      }).catch(() => {
        this.$message.info('已取消操作')
      })
    },
    
    // 下播
    handleEndLive() {
      if (!this.canUseLive) return
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      this.$confirm(
        '结束后不可恢复：主播端会被立即踢出直播间、推流中断，观众看到「直播已结束」。' +
        '如果只是这里看不到画面，请先点「重试播放」或检查推流端（OBS 码率是否正常），不要下播。',
        '确认结束直播？',
        {
          confirmButtonText: '确认结束',
          cancelButtonText: '再想想',
          type: 'error'
        }
      ).then(() => {
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive) return
        
        liveManageApi.updateLiveState({
          liveId: id,
          state: 3
        }).then(() => {
          if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive) return
          this.liveInfo.state = 3
          this.playerError = '直播已结束'
          this.clearLiveTasks()
          this.$message.success('直播已结束')
          // 延迟跳转回列表页面
          this.playerReturnTimer = setTimeout(() => {
            this.playerReturnTimer = null
            this.$router.go(-1)
          }, 1500)
        }).catch(() => {
          this.$message.error('结束直播失败')
        })
      }).catch(() => {
        this.$message.info('已取消操作')
      })
    },
    // 手动推送直播提醒（全员极光推送）
    //
    // 中控页也放一个，是因为主播已经开播之后才发现"没人进来"是常态，
    // 这时候人在中控台盯着，不该再退回直播列表页去找按钮。
    // 同样是全员推送、不可撤回，所以二次确认不能省。
    handlePushRemind() {
      if (!this.canUseLive) return
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      if (!this.liveInfo.id) {
        this.$message.error('直播ID不存在')
        return
      }
      if (!this.liveInfo.title) {
        this.$message.error('直播标题为空')
        return
      }
      this.$confirm(
        `确认向全部用户推送「${this.liveInfo.title}」的直播提醒吗？`,
        '推送直播提醒',
        { confirmButtonText: '确认推送', cancelButtonText: '取消', type: 'warning' }
      ).then(() => {
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive) return
        liveManageApi.pushRemind(id).then(() => {
          if (!this.isCurrentLiveContext(generation, id)) return
          this.$message.success('推送已发出')
        }).catch(() => {
          this.$message.error('推送失败')
        })
      }).catch(() => {})
    },

    // 设置
    handleSettings() {
      // TODO: 打开设置弹窗
      this.$message.info('设置功能待开发')
    },
    
    // 标签页切换
    handleTabClick(tab) {
      console.log('切换到标签页:', tab.name)
      if (tab.name === 'users') {
       
        this.loadUserList()
      } else if (tab.name === 'blacklist') {
    
        this.loadBlacklistUsers()
      }
    },
    
    // 加载用户列表
    async loadUserList() {
      if (!this.liveInfo.id) {
        console.warn('直播ID不存在，无法加载用户列表')
        return
      }
      
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      try {
        const params = {
          keyWord: this.userQuery.keyWord,
          liveId: this.liveInfo.id,
          pageNo: this.userQuery.pageNo,
          pageSize: this.userQuery.pageSize,
          isForbiddenSpeech: 0 // 正常用户参数
        }
        
        console.log('加载用户列表参数:', params)
        const response = await liveManageApi.getLiveLookList(params)
        if (!this.isCurrentLiveContext(generation, id)) return
        console.log('用户列表接口响应:', response)
        
        if (response.code === '0' && response.data) {
          // 接口返回的data是数组，不是对象
          this.userList = Array.isArray(response.data) ? response.data : []
          
          // 更新统计数据
          this.userStats = {
            totalViewers: this.userList.length,
            totalDuration: this.userList.reduce((sum, user) => sum + (user.timeSpan || 0), 0),
            avgDuration: this.userList.length > 0 ? Math.round(this.userList.reduce((sum, user) => sum + (user.timeSpan || 0), 0) / this.userList.length) : 0
          }
          
          console.log('用户列表数据:', this.userList)
          console.log('用户统计数据:', this.userStats)
        }
      } catch (error) {
        if (!this.isCurrentLiveContext(generation, id)) return
        console.error('加载用户列表失败:', error)
        this.$message.error('加载用户列表失败')
      }
    },
    
    // 加载小黑屋用户列表
    async loadBlacklistUsers() {
      if (!this.liveInfo.id) {
        console.warn('直播ID不存在，无法加载小黑屋用户列表')
        return
      }
      
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      try {
        const params = {
          keyWord: this.userQuery.keyWord,
          liveId: this.liveInfo.id,
          pageNo: this.userQuery.pageNo,
          pageSize: this.userQuery.pageSize,
          isForbiddenSpeech: 1 // 小黑屋用户参数
        }
        
        console.log('加载小黑屋用户列表参数:', params)
        const response = await liveManageApi.getLiveLookList(params)
        if (!this.isCurrentLiveContext(generation, id)) return
        console.log('小黑屋用户列表接口响应:', response)
        
        if (response.code === '0' && response.data) {
          // 接口返回的data是数组，不是对象
          this.blacklistUsers = Array.isArray(response.data) ? response.data : []
          console.log('小黑屋用户列表数据:', this.blacklistUsers)
        }
      } catch (error) {
        if (!this.isCurrentLiveContext(generation, id)) return
        console.error('加载小黑屋用户列表失败:', error)
        this.$message.error('加载小黑屋用户列表失败')
      }
    },
    
    // 搜索用户列表
    handleSearchUsers() {
      this.userQuery.pageNo = 1
      this.loadUserList()
    },
    
    // 搜索小黑屋用户列表
    handleSearchBlacklist() {
      this.userQuery.pageNo = 1
      this.loadBlacklistUsers()
    },
    
    // 计算观看时长
    calculateViewDuration(row) {
      // 如果outTime不为null，使用timeSpan字段
      if (row.outTime) {
        return `${row.timeSpan}分钟`
      }
      
      // 如果outTime为null，计算从jionTime到当前时间的时长
      if (row.jionTime) {
        const joinTime = new Date(row.jionTime)
        const now = new Date()
        const diffMinutes = Math.floor((now - joinTime) / (1000 * 60))
        return `${diffMinutes}分钟`
      }
      
      return '0分钟'
    },
    
    // 禁言/解禁用户
    async handleForbiddenUser(user, isForbiddenSpeech) {
      const action = isForbiddenSpeech === 1 ? '禁言' : '解禁'
      const confirmMessage = `确定要${action}用户"${user.name}"吗？`
      
      try {
        await this.$confirm(confirmMessage, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const params = {
          isForbiddenSpeech: isForbiddenSpeech,
          liveId: this.liveInfo.id,
          userId: user.userId
        }
        
        console.log(`${action}用户参数:`, params)
        const response = await liveManageApi.forbiddenUser(params)
        console.log(`${action}用户响应:`, response)
        
        if (response.code === '0') {
          this.$message.success(`${action}成功`)
          
          if (isForbiddenSpeech === 1) {
            // 禁言操作：从用户列表移除，添加到小黑屋
            this.userList = this.userList.filter(u => u.userId !== user.userId)
            // 刷新小黑屋列表
            if (this.activeTab === 'blacklist') {
              this.loadBlacklistUsers()
            }
          } else {
            // 解禁操作：从小黑屋移除，添加到用户列表
            this.blacklistUsers = this.blacklistUsers.filter(u => u.userId !== user.userId)
            // 刷新用户列表
            if (this.activeTab === 'users') {
              this.loadUserList()
            }
          }
        } else {
          this.$message.error(response.message || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error(`${action}用户失败:`, error)
          this.$message.error(`${action}失败`)
        }
      }
    },
    
    // 发送消息
    async handleSendMessage() {
      if (!this.canUseLive || !this.messageText.trim()) return
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      const im = this.im
      
      console.log('发送消息内容:', this.messageText)
      console.log('发送到群组:', this.imGroupId)
      
      if (this.imReady && this.im) {
        // 获取当前用户信息 - 使用TIM SDK的原生方法
        const tim = this.im.getTim()
        console.log('TIM实例:', tim)
        
        // getMyProfile返回的是Promise，需要等待解析
        let currentUser = null
        if (tim.getMyProfile) {
          try {
            const profileResult = await tim.getMyProfile()
            console.log('getMyProfile结果:', profileResult)
            currentUser = profileResult.data || profileResult
            console.log('当前IM用户信息:', currentUser)
            console.log('发送消息时的用户昵称:', currentUser ? currentUser.nick : '未知')
            console.log('发送消息时的用户ID:', currentUser ? currentUser.userID : '未知')
          } catch (error) {
            console.error('获取用户信息失败:', error)
          }
        }
        
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || im !== this.im) return
        im.sendTextMessage(this.imGroupId, this.messageText).then(() => {
          if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || im !== this.im) return
          console.log('消息发送成功:', this.messageText)
          console.log('发送成功时的用户信息:', currentUser)
          this.addMessage({ type: 'user-message', user: '管理员', content: this.messageText })
          this.messageText = ''
        }).catch((error) => {
          if (!this.isCurrentLiveContext(generation, id) || im !== this.im) return
          console.error('消息发送失败:', error)
          // 群组不存在错误（10010）
          if (error && error.code === 10010) {
            this.$message.error('群组不存在，无法发送消息。请检查直播配置或联系管理员。')
            this.addMessage({ type: 'system-message', content: '群组不存在，无法发送消息', timestamp: new Date().toLocaleString() })
          } else {
            this.$message.error('发送失败: ' + (error.message || '未知错误'))
          }
        })
      } else {
        console.log('IM未就绪，本地显示消息:', this.messageText)
        this.addMessage({ type: 'user-message', user: '管理员', content: this.messageText })
        this.messageText = ''
      }
    },
    
    
    // 滚动到底部
    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight
      }
    },
    
    // 初始化IM消息（真实接入）
    async initIMMessage() {
      if (!this.canUseLive || !this.imGroupId) return
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      const groupId = this.imGroupId
      try {
        //const nickName = (this.liveInfo && this.liveInfo.anchorInfo) || '管理员'
        const nickName = '管理员'      
        const resp = await request({
          url: '/im/imConversation/tee/proLoginForCustom',
          method: 'post',
          data: { nickName, userId: Date.now() + '' }
        })
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive) return
        // 兼容两种返回结构：
        // 1) { code, data: { userId, userSig } }
        // 2) { userId, userSig }
        const payload = (resp && (resp.data || resp)) || {}
        const userId = payload.userId || (payload.data && payload.data.userId)
        const userSig = payload.userSig || (payload.data && payload.data.userSig)
        if (!userId || !userSig) {
          console.warn('IM 登录接口返回异常:', resp)
          return
        }
        
        const im = new TimMessage({
          onMessageReceived: message => {
            if (this.isCurrentLiveContext(generation, id) && this.im === im) this.onMessageReceived(message)
          },
          onConversationListUpdated: () => {}
        })
        this.im = im
        await im.loginTim(userId, userSig)
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || this.im !== im) return
        console.log('IM登录成功 - userId:', userId)
        console.log('IM登录成功 - nickName:', nickName)
        
        // 检查群组ID是否有效
        if (!this.imGroupId || !this.imGroupId.trim()) {
          console.error('群组ID为空，无法加入群组')
          this.$message.warning('群组ID未配置，无法加入直播间聊天')
          return
        }
        
        console.log('准备加入群组，群组ID:', this.imGroupId)
        try {
          await im.joinGroup(groupId)
          if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || this.im !== im) return
          this.imReady = true
          this.addMessage({ type: 'system-message', content: '已加入直播间', timestamp: new Date().toLocaleString() })
          console.log('成功加入群组:', this.imGroupId)
        } catch (error) {
          if (!this.isCurrentLiveContext(generation, id) || this.im !== im) return
          console.error('加入群组失败:', error)
          if (error.isGroupNotExist || (error.code === 10010)) {
            this.$message.error('群组不存在，无法加入直播间聊天。请检查直播配置或联系管理员。')
            this.addMessage({ type: 'system-message', content: '群组不存在，无法加入直播间聊天', timestamp: new Date().toLocaleString() })
          } else {
            this.$message.error('加入群组失败: ' + (error.message || '未知错误'))
            this.addMessage({ type: 'system-message', content: '加入直播间失败: ' + (error.message || '未知错误'), timestamp: new Date().toLocaleString() })
          }
          // 群组不存在时，不设置 imReady 为 true，阻止后续操作
          return
        }
        
        // 登录成功后获取用户信息 - 使用TIM SDK的原生方法
        const tim = this.im.getTim()
        if (tim.getMyProfile) {
          try {
            const profileResult = await tim.getMyProfile()
            console.log('IM登录成功后的用户信息:', profileResult)
            const userProfile = profileResult.data || profileResult
            console.log('解析后的用户信息:', userProfile)
            console.log('用户昵称:', userProfile ? userProfile.nick : '未知')
          } catch (error) {
            console.error('获取用户信息失败:', error)
          }
        }
        // 拉取历史消息
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || this.im !== im) return
        await this.loadHistoryMessages()
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || this.im !== im) return
        
        // 初始化 IM 在线人数定时更新（每5秒更新一次）
        this.updateIMOnlineCount()
        this.imCountTimer = setInterval(() => {
          this.updateIMOnlineCount()
        }, 5000)
      } catch (e) {
        if (!this.isCurrentLiveContext(generation, id)) return
        console.warn('IM 初始化失败：', e)
      }
    },
    
    // 接收消息回调（TimMessage 调用）
    onMessageReceived(message, fromHistory = false) {
      if (!this.canUseLive || !this.matchesLiveGroup(message)) return
      console.log('========== 收到IM消息 ==========')
      console.log('消息完整对象:', message)
      
      // 检查是否是福袋相关的消息
      try {
        const TIMTypes = this.im && this.im.getTIM && this.im.getTIM().TYPES
        const isCustom = message.type === 'TIMCustomElem' || (TIMTypes && message.type === TIMTypes.MSG_CUSTOM)
        
        if (isCustom && message.payload) {
          console.log('========== 检测到自定义消息，检查是否为福袋消息 ==========')
          console.log('自定义消息payload:', message.payload)
          
          // 尝试解析 payload.data（可能是JSON字符串）
          if (message.payload.data) {
            try {
              const customData = typeof message.payload.data === 'string' 
                ? JSON.parse(message.payload.data) 
                : message.payload.data
              console.log('自定义消息解析后的data:', customData)
              
              // 检查是否是福袋消息 (businessID === "liveBlessingBag")
              if (customData.businessID === 'liveBlessingBag') {
                console.log('========== 🎁 检测到福袋消息！==========')
                console.log('福袋数据:', customData)
                
                // 处理福袋数据（可能在 list 数组中，也可能直接是对象）
                const luckyBagData = customData.list && customData.list.length > 0 
                  ? customData.list[0] 
                  : customData
                
                // 启动倒计时
                if (luckyBagData.openTime) {
                  this.printLuckyBagCountdown(luckyBagData)
                } else {
                  console.warn('福袋消息缺少 openTime 字段，无法启动倒计时')
                }
              }
         
            } catch (parseError) {
              console.log('解析payload.data失败:', parseError)
              console.log('原始payload.data:', message.payload.data)
            }
          }
          
          // 检查description字段
          if (message.payload.description) {
            console.log('自定义消息description:', message.payload.description)
            if (message.payload.description.includes('福袋') || 
                message.payload.description.toLowerCase().includes('luckybag') ||
                message.payload.description.toLowerCase().includes('blessingbag')) {
              console.log('========== 🎁 在description中发现福袋关键字！==========')
            }
          }
        }
      } catch (checkError) {
        console.warn('检查福袋消息时出错:', checkError)
      }
      try {
        // 过滤消息：只处理当前直播群组的消息
        if (this.imGroupId) {
          // 获取消息的群组ID
          let messageGroupId = null
          
          // 方式1: 从 conversationID 提取（格式：GROUP@TGS#xxx）
          if (message.conversationID) {
            const match = message.conversationID.match(/GROUP(@TGS#[^@]+)/)
            if (match && match[1]) {
              messageGroupId = match[1]
            }
          }
          
          // 方式2: 从 groupID 字段获取
          if (!messageGroupId && message.groupID) {
            messageGroupId = message.groupID
          }
          
          // 方式3: 从 group 属性获取
          if (!messageGroupId && message.group && message.group.groupID) {
            messageGroupId = message.group.groupID
          }
          
          // 方式4: 从 to 字段获取（群组消息的接收者就是群组ID）
          if (!messageGroupId && message.to) {
            messageGroupId = message.to
          }
          // 如果消息有群组ID且不匹配当前直播群组，则忽略该消息
          if (messageGroupId && messageGroupId !== this.imGroupId) {
            console.log('消息群组ID不匹配，忽略该消息。消息群组:', messageGroupId, '当前群组:', this.imGroupId)
            return
          }
          
          // 如果无法获取消息的群组ID，为了安全起见，也忽略该消息（避免显示其他群组消息）
          // 但如果是在拉取历史消息时，因为已经通过 conversationID 过滤了，所以可以显示
          if (!messageGroupId && !message.conversationID) {
            console.log('无法获取消息群组ID且无conversationID，忽略该消息')
            return
          }
        }
        
        const TIMTypes = this.im && this.im.getTIM && this.im.getTIM().TYPES
        const isText = message.type === 'TIMTextElem' || (TIMTypes && message.type === TIMTypes.MSG_TEXT)
        const isCustom = message.type === 'TIMCustomElem' || (TIMTypes && message.type === TIMTypes.MSG_CUSTOM)
        if (isText) {
          // 获取用户ID，优先使用 fromAccount，其次使用 from
          const userId = message.fromAccount || message.from || null
          this.addMessage({ 
            type: 'user-message', 
            user: message.nick || message.from || '陌生人', 
            userId: userId,
            content: message.payload.text 
          })
        } else if (isCustom) {
          const businessID = this.getCustomBusinessID(message)
          if (businessID === 'customer_live_state') {
            if (!fromHistory) this.applyLiveStateMessage(message)
            return
          }
          // 虚拟人数变更后立即同步，中控无需刷新页面
          if (businessID === 'customer_live_addVirtual') {
            this.applyVirtualCountChange(message)
          }
          // 客户进入直播间的提醒不展示，中控只看有效消息
          if (HIDDEN_BUSINESS_IDS.indexOf(businessID) !== -1) {
            console.log('已屏蔽自定义消息，businessID:', businessID)
            return
          }
          this.addMessage({ type: 'system-message', content: message.payload.description || '[自定义消息]' })
        }
      } catch (e) {
        console.warn('解析消息失败', e)
      }
    },
    matchesLiveGroup(message) {
      if (!message || !this.imGroupId) return false
      const groupId = (message.conversationID || '').replace(/^GROUP/, '') || message.groupID || (message.group && message.group.groupID) || message.to
      return groupId === this.imGroupId
    },
    applyLiveStateMessage(message) {
      if (!this.canUseLive || !this.matchesLiveGroup(message)) return
      try {
        const data = typeof message.payload.data === 'string' ? JSON.parse(message.payload.data) : message.payload.data
        if (!data || (data.liveId && String(data.liveId) !== String(this.liveInfo.id))) return
        // Android sendLiveState 的实际 liveType 为 1继续、2暂停、3结束；不推测未知类型。
        if (!['1', '2', '3'].includes(String(data.liveType))) return
        this.liveInfo.state = Number(data.liveType)
        if (this.liveInfo.state === 3) {
          this.playerError = '直播已结束'
          this.clearLiveTasks()
        }
      } catch (error) {
        console.warn('直播状态消息格式无效', error)
      }
    },
    // 虚拟人数变更消息：后台传的是增量差值，累加到当前值上
    //
    // 原来中控台的观看人数只在进页面时取一次，主播端/用户端那边虚拟人数一直在涨，
    // 中控看到的还是刚进来时的旧值，运营只能靠刷新页面才知道真实人数，
    // 想据此判断"要不要发福袋拉人"根本没法用。这里收到 IM 广播就地累加。
    //
    // 注意后台发的是增量不是全量，所以必须累加而不是覆盖；下限夹到 0，
    // 防止后台补发负增量时把人数显示成负数。
    applyVirtualCountChange(message) {
      try {
        const payload = message.payload
        const customData = typeof payload.data === 'string'
          ? JSON.parse(payload.data)
          : payload.data
        // 只处理当前直播的消息
        if (customData.liveId && this.liveInfo.id &&
            String(customData.liveId) !== String(this.liveInfo.id)) {
          return
        }
        const addCount = Number(customData.addVirtualcount) || 0
        if (!addCount) return
        const current = this.liveInfo.lookUserVirtualcount || 0
        this.liveInfo.lookUserVirtualcount = Math.max(0, current + addCount)
        console.log('虚拟人数变更:', addCount, '当前虚拟人数:', this.liveInfo.lookUserVirtualcount)
      } catch (e) {
        console.warn('解析虚拟人数消息失败', e)
      }
    },
    // 取自定义消息的 businessID，取不到返回空字符串
    getCustomBusinessID(message) {
      try {
        const payload = message && message.payload
        if (!payload || !payload.data) return ''
        const customData = typeof payload.data === 'string'
          ? JSON.parse(payload.data)
          : payload.data
        return (customData && customData.businessID) || ''
      } catch (e) {
        return ''
      }
    },
    onConversationListUpdated() {},
    
    // 打印福袋倒计时（独立函数，每秒打印一次）
    printLuckyBagCountdown(luckyBagData) {
      if (!this.canUseLive) return
      const bagId = luckyBagData.id
      if (!bagId) {
        console.warn('福袋数据缺少 id，无法启动倒计时')
        return
      }
      
      // 如果该福袋已经有倒计时在运行，先清除
      if (this.luckyBagCountdownTimers[bagId]) {
        clearInterval(this.luckyBagCountdownTimers[bagId])
        delete this.luckyBagCountdownTimers[bagId]
      }
      
      const openTime = luckyBagData.openTime
      if (!openTime) {
        console.warn('福袋数据缺少 openTime，无法启动倒计时')
        return
      }
      
      // 计算倒计时的函数
      const updateCountdown = () => {
        const now = new Date()
        const openTimeDate = new Date(openTime)
        const diff = openTimeDate.getTime() - now.getTime()
        
        if (diff <= 0) {
          // 倒计时结束
          console.log('========== 🎁 福袋已开启！==========')
          console.log('福袋ID:', bagId)
          if (this.luckyBagCountdownTimers[bagId]) {
            clearInterval(this.luckyBagCountdownTimers[bagId])
            delete this.luckyBagCountdownTimers[bagId]
          }
          return
        }
        
        // 3秒 = 3000毫秒
        const threeSeconds = 3 * 1000
        
        // 倒计时结束前3秒，主动请求开奖接口（只请求一次）
        if (diff <= threeSeconds && diff > 0 && !this.luckyBagLotteryRequested[bagId]) {
          this.luckyBagLotteryRequested[bagId] = true
          console.log(`========== 🎁 倒计时剩余3秒，主动请求福袋开奖接口 [ID: ${bagId}] ==========`)
          this.requestLuckyBagLottery(bagId)
        }
        
        // 3分钟 = 180秒 = 180000毫秒
        const threeMinutes = 3 * 60 * 1000
        
        // 只有当剩余时间小于等于3分钟时才开始打印
        if (diff > threeMinutes) {
          return // 不打印，但继续运行定时器
        }
        
        // 如果是第一次进入3分钟倒计时，打印提示
        if (diff <= threeMinutes && diff > threeMinutes - 1000) {
          console.log('========== 🎁 福袋倒计时开始（剩余3分钟）==========')
          console.log('福袋ID:', bagId)
          console.log('开奖时间:', openTime)
        }
        
        // 计算剩余时间
        const days = Math.floor(diff / (1000 * 60 * 60 * 24))
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((diff % (1000 * 60)) / 1000)
        
        // 格式化倒计时显示
        let countdownStr = ''
        if (days > 0) {
          countdownStr += `${days}天 `
        }
        if (hours > 0 || days > 0) {
          countdownStr += `${hours.toString().padStart(2, '0')}:`
        }
        countdownStr += `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        
        // 打印倒计时（只在剩余3分钟内打印）
        console.log(`🎁 福袋倒计时 [ID: ${bagId}]: ${countdownStr}`)
      }
      
      // 每秒更新一次（即使不打印也会运行，以便在3分钟时自动开始打印）
      this.luckyBagCountdownTimers[bagId] = setInterval(updateCountdown, 1000)
      
      // 立即执行一次检查
      updateCountdown()
    },
    
    // 请求福袋开奖接口
    async requestLuckyBagLottery(bagId) {
      if (!this.canUseLive) return
      try {
        const response = await liveBlessingBagApi.lotteryDraw({ id: bagId })
        if (response.code === '0') {
          console.log(`========== 🎁 福袋开奖请求成功 [ID: ${bagId}] ==========`)
          console.log('响应数据:', response.data)
        } else {
          console.warn(`========== 🎁 福袋开奖请求失败 [ID: ${bagId}] ==========`)
          console.warn('错误信息:', response.message || '未知错误')
        }
      } catch (error) {
        console.error(`========== 🎁 福袋开奖请求出错 [ID: ${bagId}] ==========`)
        console.error('错误详情:', error)
      }
    },
    
    // 获取福袋列表并启动倒计时
    async loadLuckyBagList() {
      if (!this.canUseLive) {
        console.warn('缺少直播ID，无法获取福袋列表')
        return
      }
      
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      try {
        const response = await liveBlessingBagApi.fetchList({
          liveId: id,
          createdTimeSortSign:-1,
          status:10
        })
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive) return
        
        if (response.code === '0' && response.data && response.data.list) {
          const luckyBagList = response.data.list
          console.log('========== 获取到福袋列表 ==========')
          console.log('福袋数量:', luckyBagList.length)
          
          // 遍历福袋列表，为每个福袋启动倒计时
          luckyBagList.forEach(bag => {
            if (bag.openTime) {
              this.printLuckyBagCountdown(bag)
            } else {
              console.warn(`福袋 [ID: ${bag.id}] 缺少 openTime，跳过倒计时`)
            }
          })
        } else {
          console.warn('获取福袋列表失败或列表为空:', response)
        }
      } catch (error) {
        console.error('获取福袋列表出错:', error)
      }
    },
    
    // 获取并更新 IM 在线人数
    async updateIMOnlineCount() {
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      const im = this.im
      try {
        if (!this.canUseLive || !im || !this.imReady || !this.imGroupId) {
          return
        }
        const count = await im.getGroupOnlineMemberCount(this.imGroupId)
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || this.im !== im) return
        this.imOnlineCount = count || 0
      } catch (error) {
        if (!this.isCurrentLiveContext(generation, id) || this.im !== im) return
        // 群组不存在错误（10010）
        if (error && error.code === 10010) {
          // 群组不存在时，不更新在线人数，保持之前的值或显示为0
          this.imOnlineCount = 0
          // 如果是首次获取失败，可以考虑提示用户
          if (this.imCountTimer) {
            // 已经有定时器在运行，说明不是首次，静默处理
          }
        } else {
          console.warn('获取 IM 在线人数失败:', error)
          this.imOnlineCount = 0
        }
      }
    },

    // 拉取历史消息（首次进入）
    async loadHistoryMessages() {
      const generation = this.liveContextGeneration
      const id = this.liveInfo.id
      const im = this.im
      try {
        if (!this.canUseLive || !im || !this.imReady) return
        const tim = im.getTim()
        // 真正的会话ID格式：GROUP@TGS#xxx
        const conversationID = 'GROUP' + this.imGroupId
        const res = await tim.getMessageList({ conversationID, count: 20 })
        if (!this.isCurrentLiveContext(generation, id) || !this.canUseLive || this.im !== im) return
        console.log("历史消息列表")
        console.log(res)
        const list = (res && res.data && res.data.messageList) || []
        // messageList 顺序为从新到旧，反转后再渲染
        list.reverse().forEach(msg => this.onMessageReceived(msg, true))
      } catch (e) {
        console.warn('拉取历史消息失败', e)
      }
    },
    
    // 添加消息到聊天记录
    addMessage(message) {
      this.chatMessages.push({
        type: message.type || 'user-message',
        user: message.user || '',
        userId: message.userId || null,
        content: message.content,
        timestamp: message.timestamp || new Date().toLocaleString()
      })
      
      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    
    // 点击用户昵称
    handleClickUserName(message) {
      if (!message.userId) {
        this.$message.warning('无法获取用户ID，无法禁言')
        return
      }
      
      // 显示禁言弹窗
      this.showForbiddenDialog(message.user, message.userId)
    },
    
    // 显示禁言弹窗
    showForbiddenDialog(userName, userId) {
      this.$confirm(
        `确定要禁言用户 "${userName}" 吗？`,
        '禁言用户',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
              instance.confirmButtonLoading = true
              instance.confirmButtonText = '禁言中...'
              
              // 调用禁言接口
              this.forbiddenUserFromChat(userName, userId)
                .then(() => {
                  done()
                  instance.confirmButtonLoading = false
                })
                .catch(() => {
                  instance.confirmButtonLoading = false
                  instance.confirmButtonText = '确定'
                })
            } else {
              done()
            }
          }
        }
      ).catch(() => {
        // 用户取消
      })
    },
    
    // 从聊天消息中禁言用户
    async forbiddenUserFromChat(userName, userId) {
      if (!this.liveInfo.id) {
        this.$message.error('直播ID不存在')
        return
      }
      
      // 从IM获取的userId可能是 "131097_229702_20" 格式，只需要传递第一部分 "131097"。
      // 非生产环境 ID 前面还有一段环境前缀（t_131097_229702_20），
      // 直接取第 0 段会把前缀当成 userId 发给后端，禁言禁不到人。
      const userIdToSend = imIdUserId(userId)
      
      const params = {
        isForbiddenSpeech: 1,
        liveId: this.liveInfo.id,
        userId: userIdToSend
      }
      
      try {
        const response = await liveManageApi.forbiddenUser(params)
        if (response.code === '0') {
          this.$message.success(`已禁言用户 "${userName}"`)
          // 刷新用户列表和小黑屋列表
          if (this.activeTab === 'users') {
            this.loadUserList()
          } else if (this.activeTab === 'blacklist') {
            this.loadBlacklistUsers()
          }
        } else {
          this.$message.error(response.message || '禁言失败')
        }
      } catch (error) {
        console.error('禁言用户失败:', error)
        this.$message.error('禁言失败')
      }
    },
    
    // 开始播放
    startPlay() {
      if (!this.canUseLive || this.playerClosed || !this.player) {
        console.error('播放器未初始化')
        return
      }
      
      console.log('用户点击开始播放')
      this.cancelPlayerFallback()
      this.playerError = '正在加载直播…'
      return this.requestPlayerPlay()
    },
    // 全屏控制
    toggleFullscreen() {
      const container = this.$el.querySelector('.video-container')
      const isFull = document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement
      if (isFull) {
        if (document.exitFullscreen) document.exitFullscreen()
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen()
        else if (document.msExitFullscreen) document.msExitFullscreen()
      } else if (container) {
        if (container.requestFullscreen) container.requestFullscreen()
        else if (container.webkitRequestFullscreen) container.webkitRequestFullscreen()
        else if (container.msRequestFullscreen) container.msRequestFullscreen()
      }
    },
    
    // 重试播放
    retryPlay() {
      if (!this.canUseLive || this.playerClosed) return
      this.cancelPlayerFallback()
      this.playerAttemptedUrls = []
      this.hasTriedAllUrls = false
      const currentUrl = toProxyUrl(this.playerCurrentUrl)
      const nextUrl = this.getFallbackUrls().find(url => url !== currentUrl)
      if (nextUrl && this.player) return this.switchPlayerUrl(nextUrl)
      return this.createPlayer(undefined, true, true)
    },
    
    // 手动测试播放
    testPlay() {
      const playUrl = getBestPlayUrl(this.streamUrls)
      if (playUrl) {
        console.log('手动测试播放:', playUrl)
        this.createPlayer()
      } else {
        console.error('没有可用的播放地址')
        this.$message.error('没有可用的播放地址')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.live-console-container {
  height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  ::v-deep .live-player-dimensions{
    width: 100% !important;
    height: 100% !important;
  }
}

.console-header {
  background: #fff;
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e6e6e6;
  
  .breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #666;
    
    .close-btn {
      margin-left: 20px;
      font-size: 18px;
      cursor: pointer;
      color: #999;
      
      &:hover {
        color: #333;
      }
    }
  }
  
  .header-actions {
    display: flex;
    gap: 15px;
    
    i {
      font-size: 18px;
      color: #666;
      cursor: pointer;
      
      &:hover {
        color: #409EFF;
      }
    }
  }
}

.console-content {
  flex: 1;
  display: flex;
  padding: 20px;
  gap: 20px;
}

.left-panel {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.action-buttons {
  margin-bottom: 20px;
  
  .el-button {
    margin-right: 10px;
  }
}

.live-info {
  margin-bottom: 20px;
  
  .info-item {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    
    .label {
      font-weight: 500;
      color: #333;
      min-width: 80px;
    }
    
    .value {
      color: #666;
      margin-right: 10px;
    }
  }
}

.video-player {
  flex: 1;
  display: flex;
  flex-direction: column;
  
  // 竖屏模式优化
  &.vertical-mode {
    .video-container {
      // 竖屏直播使用16:9比例，但保持高度自适应
      aspect-ratio: 9/16;
      max-height: 70vh; // 限制最大高度，避免占用过多空间
      
      video {
        object-fit: contain; // 竖屏使用contain，确保完整显示
        object-position: center;
      }
    }
  }
  
  // 横屏模式优化
  &.horizontal-mode {
    .video-container {
      // 横屏直播使用16:9比例
      aspect-ratio: 16/9;
      
      video {
        object-fit: cover; // 横屏使用cover，填满容器
      }
    }
  }
  
  .video-container {
    flex: 1;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
    position: relative;

    .player-mount {
      width: 100%;
      height: 100%;
    }
    display: flex;
    flex-direction: column;
    height: 100%;
    
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .player-error-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      
      .error-content {
        text-align: center;
        color: #fff;
        
        i {
          font-size: 48px;
          color: #f56c6c;
          margin-bottom: 20px;
          display: block;
        }
        
        p {
          font-size: 16px;
          margin-bottom: 20px;
          color: #fff;
        }
        
        .play-button {
          background: linear-gradient(45deg, #409EFF, #67C23A);
          border: none;
          padding: 12px 24px;
          font-size: 16px;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
          transition: all 0.3s ease;
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(64, 158, 255, 0.4);
          }
          
          i {
            margin-right: 8px;
            font-size: 18px;
          }
        }
        
        .el-button {
          margin-top: 10px;
        }
      }
    }
  }
}

/* 覆盖 TCPlayer 默认高度（100px）为填充容器高度 */
::v-deep .liveplayer-dimensions {
  width: 100% !important;
  height: 100% !important;
}

/* 让内部 video 画面铺满容器 */
::v-deep .vjs-tech {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}

/* 竖屏模式下的TCPlayer优化 */
.vertical-mode ::v-deep .vjs-tech {
  object-fit: contain !important;
  object-position: center !important;
}

/* 横屏模式下的TCPlayer优化 */
.horizontal-mode ::v-deep .vjs-tech {
  object-fit: cover !important;
}

.right-panel {
  width: 400px;
  background: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

.management-tabs {
  border-bottom: 1px solid #e6e6e6;
  
  ::v-deep .el-tabs__header {
    margin: 0;
  }
  
  ::v-deep .el-tabs__nav-wrap {
    padding: 0 20px;
  }
  
  ::v-deep .el-tabs__item {
    padding: 0 15px;
    height: 50px;
    line-height: 50px;
  }
  
  ::v-deep .el-tabs__item.is-active {
    color: #409EFF;
    font-weight: 500;
  }
}

.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  
  .message-item {
    margin-bottom: 10px;
    font-size: 14px;
    line-height: 1.5;
    
    &.system-message {
      color: #999;
      font-style: italic;
      
      .timestamp {
        margin-right: 10px;
      }
    }
    
    &.muted-message {
      color: #f56c6c;
      
      .timestamp {
        margin-right: 10px;
      }
    }
    
    &.user-message {
      .user {
        color: #409EFF;
        font-weight: 500;
        margin-right: 5px;
        
        &.clickable-user {
          cursor: pointer;
          text-decoration: underline;
          transition: color 0.3s;
          
          &:hover {
            color: #67C23A;
          }
        }
      }
      
      .content {
        color: #333;
      }
    }
    
    &.admin-message {
      .user {
        color: #67C23A;
        font-weight: 500;
        margin-right: 5px;
      }
      
      .content {
        color: #333;
      }
    }
  }
}

.message-input {
  padding: 15px;
  border-top: 1px solid #e6e6e6;
  display: flex;
  gap: 10px;
  
  .el-input {
    flex: 1;
  }
  
  .el-button {
    width: 60px;
  }
}

// 滚动条样式
.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// 用户标签页样式
.user-stats {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 15px;
  
  .stat-item {
    display: inline-block;
    margin-right: 20px;
    margin-bottom: 8px;
    
    .stat-label {
      font-size: 14px;
      color: #666;
      font-weight: 500;
    }
    
    .stat-value {
      font-size: 14px;
      color: #333;
      font-weight: 600;
    }
  }
}

.search-box {
  padding: 15px;
  border-bottom: 1px solid #e6e6e6;
  
  .el-input {
    width: 100%;
  }
}

.user-list {
  flex: 1;
  overflow: hidden;
  
  ::v-deep .el-table {
    font-size: 12px;
    
    .el-table__header {
      th {
        background: #f5f7fa;
        color: #606266;
        font-weight: 500;
        padding: 8px 0;
      }
    }
    
    .el-table__body {
      td {
        padding: 8px 0;
        border-bottom: 1px solid #ebeef5;
      }
    }
  }
  
  .user-info {
    display: flex;
    flex-direction: column;
    gap: 1px;
    
    .user-name {
      font-weight: 500;
      color: #333;
      font-size: 12px;
      line-height: 1.2;
      word-break: break-all;
    }
    
    .user-level {
      font-size: 10px;
      color: #909399;
      background: #f0f2f5;
      padding: 1px 3px;
      border-radius: 2px;
      display: inline-block;
      width: fit-content;
      line-height: 1.1;
      word-break: break-all;
    }
    
    .user-phone {
      font-size: 10px;
      color: #909399;
      line-height: 1.1;
      word-break: break-all;
    }
  }
}
</style>
